import {
  init,
  track,
  setUserId,
  identify,
  Identify,
  flush,
  getDeviceId,
} from "@amplitude/analytics-browser";
import UAParser from "ua-parser-js";

import { getSiteInfo } from "@/sites";
import { isClient } from "@/utils/browser";
import { setCookie } from "@/services/CookiesService";
import { AMPLITUDE_DEVICE_ID_COOKIE } from "@/internals/constants/cookies";
import { sanitizeAmplitudeDeviceId } from "./deviceId";

const { domainName } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN);

// RegExp matches referring host + subdomains (SDK matches hostname, not full URL paths).
const EXCLUDED_REFERRER_DOMAINS: RegExp[] = [
  /(^|\.)qrcreator\.com$/,
  /(^|\.)qrcreator\.dev$/,
  /(^|\.)qrnow\.pro$/,
  /(^|\.)pay\.nicepay\.co\.kr$/,
  /(^|\.)stripe\.com$/,
  /(^|\.)paypal\.com$/,
];

export const initAmplitude = () => {
  const amplitudeKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY || "";

  if (!amplitudeKey || !isClient()) return;

  const initResult = init(amplitudeKey, undefined, {
    serverZone: "EU",
    cookieOptions: {
      domain: `.${domainName}`,
      expiration: 365,
      sameSite: "Lax",
      secure: true,
    },
    autocapture: {
      attribution: {
        excludeInternalReferrers: true,
        excludeReferrers: EXCLUDED_REFERRER_DOMAINS,
        resetSessionOnNewCampaign: true,
      },
      pageViews: false,
      sessions: false,
      formInteractions: false,
      fileDownloads: false,
      pageUrlEnrichment: true,
      elementInteractions: false,
      webVitals: false,
    },
  });

  amplitudeDeviceType();

  initResult?.promise?.then(() => persistAmplitudeDeviceIdCookie()).catch(() => undefined);
};

export const amplitudeDeviceType = () => {
  const parser: UAParser = new UAParser();
  const userAgent: UAParser.IResult = parser.getResult();

  let deviceType = "Unknown";
  if (userAgent.device.type === "mobile") {
    deviceType = "Mobile";
  } else if (userAgent.device.type === "tablet") {
    deviceType = "Tablet";
  } else {
    deviceType = "Desktop";
  }

  const identifyEvent = new Identify();
  identifyEvent.set("User Device", deviceType);

  identify(identifyEvent);
};

export const amplitudeTrackEvent = (name: string, data?: Record<string, any>) => {
  if (process.env.NEXT_PUBLIC_ENV === "local") return;

  const identifyEvent = new Identify();
  identifyEvent.set("domain", domainName);
  identify(identifyEvent);

  const eventData = { ...data };
  track(name, eventData);
};

export const amplitudeTrackUser = (userId: string) => {
  if (process.env.NEXT_PUBLIC_ENV === "local") return;
  setUserId(userId);
  const ident = new Identify().set("User Id", userId);
  identify(ident);
};

export const amplitudeLogoutUser = async () => {
  await flush().promise;
  setUserId(undefined);
};

export const amplitudeGetDeviceId = (): string | undefined => {
  if (process.env.NEXT_PUBLIC_ENV === "local" || !isClient()) return undefined;
  return getDeviceId?.();
};

const AMPLITUDE_DEVICE_ID_COOKIE_TTL_MS = 365 * 24 * 60 * 60 * 1000;

export const persistAmplitudeDeviceIdCookie = (): string | undefined => {
  const deviceId = sanitizeAmplitudeDeviceId(amplitudeGetDeviceId());
  if (!deviceId) return undefined;

  try {
    setCookie(
      AMPLITUDE_DEVICE_ID_COOKIE,
      deviceId,
      new Date(Date.now() + AMPLITUDE_DEVICE_ID_COOKIE_TTL_MS)
    );
  } catch {}

  return deviceId;
};
